import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SignInButton, SignedIn, SignedOut, UserButton } from '@clerk/clerk-react';
import './CustomHTML.css';

const CustomHTML = () => {
  // Handle billing toggle
  const handleBillingToggle = (event) => {
    const isAnnual = event.target.checked;
    const monthlyPrices = document.querySelectorAll('.monthly-price');
    const annualPrices = document.querySelectorAll('.annual-price');
    
    if (isAnnual) {
      monthlyPrices.forEach(price => price.style.display = 'none');
      annualPrices.forEach(price => price.style.display = 'block');
    } else {
      monthlyPrices.forEach(price => price.style.display = 'block');
      annualPrices.forEach(price => price.style.display = 'none');
    }
  };

  // Initialize toggle functionality
  React.useEffect(() => {
    const toggle = document.getElementById('billing-toggle');
    if (toggle) {
      toggle.addEventListener('change', handleBillingToggle);
      
      // Initialize annual pricing display (since toggle defaults to checked)
      const monthlyPrices = document.querySelectorAll('.monthly-price');
      const annualPrices = document.querySelectorAll('.annual-price');
      
      monthlyPrices.forEach(price => price.style.display = 'none');
      annualPrices.forEach(price => price.style.display = 'block');
      
      return () => toggle.removeEventListener('change', handleBillingToggle);
    }
  }, []);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Add smooth scrolling for navigation links
    document.querySelectorAll('a[href^="#"]:not([href="#"])').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const targetId = this.getAttribute('href');
        if (targetId && targetId !== '#') {
          const target = document.querySelector(targetId);
          if (target) {
            target.scrollIntoView({
              behavior: 'smooth'
            });
          }
        }
      });
    });

    // Add click tracking for CTAs - redirect to signup
    document.querySelectorAll('.hero-cta, .btn-trial').forEach(button => {
      button.addEventListener('click', function(e) {
        // Only handle non-Link elements (plain anchor tags)
        if (this.tagName === 'A' && !this.classList.contains('btn-trial')) {
          e.preventDefault();
          navigate('/signup');
        }
      });
    });

    // Other existing useEffect code...
  }, [navigate]);

  return (
    <div className="custom-html-container">
      {/* Header/Navigation */}
      <header className="header">
        <nav className="nav-container">
          <Link to="/" className="logo">StudioIQ</Link>
          <ul className="nav-links">
            <li><a href="#features">Features</a></li>
            <li><a href="#pricing">Pricing</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
          <div className="auth-buttons">
            <SignedOut>
              <SignInButton mode="modal">
                <button className="btn-login">Login</button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <UserButton 
                afterSignOutUrl="/"
                appearance={{
                  elements: {
                    avatarBox: "w-8 h-8",
                    userButtonPopoverCard: "bg-white shadow-lg border",
                    userButtonPopoverActions: "bg-white"
                  }
                }}
              />
            </SignedIn>
            <Link to="/signup" className="btn-trial">Get Started</Link>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">INDUSTRY LEADING</div>
          <h1>Jewelry Repair + Restoration Pricing Technology</h1>
          <p className="subtitle">Generate accurate, professional repair quotes in seconds with AI-powered solutions built by industry experts. Take control of your pricing with intelligent tools that protect your margins.</p>
          <Link to="/signup" className="hero-cta">Get Started</Link>
          
          <div className="trust-indicators">
            ✓ Built by industry experts &nbsp;&nbsp;•&nbsp;&nbsp; ✓ Pays for itself in 30 days &nbsp;&nbsp;•&nbsp;&nbsp; ✓ Comprehensive repair database
          </div>

          <div className="problem-bullets">
            <h3>Transform your repair quoting process</h3>
            <ul className="problem-list">
              <li>Stop undercharging for complex repairs with built-in margin protection</li>
              <li>Eliminate time-consuming manual calculations and outdated books</li>
              <li>Build customer confidence with consistent, professional estimates</li>
              <li>Access the most up-to-date repair database in the industry</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <h2>Everything You Need to Price Repairs Confidently</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>Built-in Margin Protection</h3>
              <p>Never undercharge for complex repairs again. Our intelligent pricing algorithms ensure profitable margins on every job, typically paying for itself within 30 days.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💎</div>
              <h3>Industry Expert Development</h3>
              <p>Developed by seasoned jewelry industry professionals who understand the nuances of repair pricing and the challenges you face daily.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📚</div>
              <h3>Comprehensive Repair Database</h3>
              <p>Access the most up-to-date, comprehensive repair database in the industry. No more outdated books or inconsistent estimates.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎛️</div>
              <h3>Customizable Pricing Control</h3>
              <p>Maintain complete control over your pricing strategy. Adjust mark-ups, set custom parameters, and tailor quotes to match your business model and customer expectations.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Lightning Fast Quotes</h3>
              <p>Generate professional repair quotes in seconds, not minutes. Spend more time with customers and less time on calculations.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📱</div>
              <h3>Universal Device Access</h3>
              <p>Access from any device - desktop, tablet, or phone. Perfect for front-of-house customer interactions and mobile assessments.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing">
        <div className="container">
          <h2>Simple, Transparent Pricing That Pays for Itself</h2>
          
          {/* Billing Toggle */}
          <div className="billing-toggle">
            <span className="toggle-label">Monthly</span>
            <label className="toggle-switch">
              <input type="checkbox" id="billing-toggle" defaultChecked={true} />
              <span className="slider"></span>
            </label>
            <span className="toggle-label">Annual <span className="save-badge">Save 20%</span></span>
          </div>
          
          <div className="pricing-grid">
            <div className="pricing-card">
              <h3>Starter</h3>
              <div className="price-display">
                <div className="price monthly-price" style={{display: 'none'}}>$47<span>/month</span></div>
                <div className="price annual-price" style={{display: 'block'}}>$39<span>/month</span></div>
                <div className="annual-note annual-price" style={{display: 'block'}}>Billed annually ($468/year)</div>
              </div>
              <ul>
                <li>1 user, 1 store location</li>
                <li>500 AI-powered quotes per month</li>
                <li>Unlimited manual quoting</li>
                <li>Full repair database access</li>
                <li>Manual spot price entry</li>
                <li>Basic calendar export (iCal)</li>
                <li>Email support</li>
              </ul>
              <Link to="/signup" className="btn-pricing">Get Started</Link>
            </div>
            <div className="pricing-card featured">
              <div className="popular-badge">Most Popular</div>
              <h3>Pro</h3>
              <div className="price-display">
                <div className="price monthly-price" style={{display: 'none'}}>$95<span>/month</span></div>
                <div className="price annual-price" style={{display: 'block'}}>$79<span>/month</span></div>
                <div className="annual-note annual-price" style={{display: 'block'}}>Billed annually ($948/year)</div>
              </div>
              <ul>
                <li>Up to 3 users, 2 store locations</li>
                <li>1,200 AI-powered quotes per month</li>
                <li>Unlimited manual quoting</li>
                <li>Live spot price updates (Kitco API)</li>
                <li>Admin-only settings panel</li>
                <li>User role permissions</li>
                <li>Google Calendar sync</li>
                <li>Priority email support</li>
              </ul>
              <Link to="/signup" className="btn-pricing">Get Started</Link>
            </div>
            <div className="pricing-card">
              <h3>Enterprise</h3>
              <div className="price-display">
                <div className="price monthly-price" style={{display: 'none'}}>$240<span>/month</span></div>
                <div className="price annual-price" style={{display: 'block'}}>$199<span>/month</span></div>
                <div className="annual-note annual-price" style={{display: 'block'}}>Billed annually ($2,388/year)</div>
              </div>
              <ul>
                <li>Unlimited users & store locations</li>
                <li>Unlimited AI-powered quotes</li>
                <li>Unlimited manual quoting</li>
                <li>Live market pricing (all metals)</li>
                <li>Admin dashboards & analytics</li>
                <li>Role-based access controls</li>
                <li>Wage mapping by job type</li>
                <li>Dedicated account manager</li>
              </ul>
              <Link to="/signup" className="btn-pricing">Get Started</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <h2>Ready to Transform Your Repair Quoting?</h2>
          <p>Join hundreds of jewelry professionals who use StudioIQ to generate accurate quotes, protect their margins, and grow their business with our comprehensive repair database.</p>
          <div className="contact-actions">
            <Link to="/signup" className="btn-primary">Get Started</Link>
            <div className="contact-info">
              <p>Questions? Email us at <a href="mailto:support@studioiq.co">support@studioiq.co</a></p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How quickly does StudioIQ pay for itself?</h3>
              <p>Most jewelry stores see a return on investment within 30 days. By preventing just a few underpriced complex repairs, StudioIQ typically covers its monthly cost and generates additional profit.</p>
            </div>
            
            <div className="faq-item">
              <h3>Can I customize pricing and mark-ups for my business?</h3>
              <p>Absolutely! StudioIQ gives you complete control over your pricing strategy. You can adjust mark-ups, set custom parameters, and tailor quotes to match your specific business model and market positioning.</p>
            </div>
            
            <div className="faq-item">
              <h3>How comprehensive is the repair database?</h3>
              <p>Our database includes thousands of repair types, from simple ring sizing to complex restoration work. It's continuously updated by industry experts and covers everything from basic repairs to specialized vintage and luxury jewelry restoration.</p>
            </div>
            
            <div className="faq-item">
              <h3>Will my staff need training to use StudioIQ?</h3>
              <p>StudioIQ is designed to be intuitive and user-friendly. Most staff members can start generating accurate quotes within minutes. Enterprise plan customers receive comprehensive onboarding support and dedicated training materials.</p>
            </div>
            
            <div className="faq-item">
              <h3>Does StudioIQ work on mobile devices?</h3>
              <p>Yes! StudioIQ works perfectly on desktop, tablet, and phone. This makes it ideal for front-of-house customer interactions, mobile assessments, and when you're away from your main workstation.</p>
            </div>
            
            <div className="faq-item">
              <h3>Can I integrate StudioIQ with my existing POS system?</h3>
              <p>StudioIQ is designed to work alongside your existing systems. While we don't currently offer direct POS integrations, our quotes can be easily exported or manually entered into most POS and management systems.</p>
            </div>
            
            <div className="faq-item">
              <h3>Is my data secure with StudioIQ?</h3>
              <p>Yes, we take data security seriously. All data is encrypted in transit and at rest, and we follow industry best practices for data protection. Your customer information and business data are completely secure.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <Link to="/" className="logo">StudioIQ</Link>
              <p>Professional jewelry repair and restoration pricing technology platform built by industry experts for independent jewelry stores.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <ul>
                  <li><a href="#features">Features</a></li>
                  <li><a href="#pricing">Pricing</a></li>
                  <li><a href="#faq">FAQ</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                  <li><Link to="/terms-of-service">Terms of Service</Link></li>
                  <li><a href="#contact">Contact</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 StudioIQ. All rights reserved.</p>
          </div>
        </div>
      </footer>
      
    </div>
  );
};

export default CustomHTML;